import { useState } from "react";
import jsPDF from "jspdf";

const priceTable = {
  '1C': [4.50, 3.50, 3.00, 2.95, 2.25, 2.00, 1.95, 1.75, 1.50, 1.25, 0.95],
  '2C': [6.00, 4.50, 4.40, 4.00, 3.04, 2.75, 2.50, 1.95, 1.75, 1.50, 1.15],
  '3C': [8.00, 6.50, 5.40, 4.85, 3.95, 3.62, 2.94, 2.35, 1.95, 1.68, 1.35],
  '4C': [9.00, 8.50, 6.60, 5.75, 4.86, 4.34, 3.59, 2.52, 2.31, 1.75, 1.51],
};

const quantityRanges = [12, 25, 36, 72, 144, 216, 288, 500, 750, 1250, 3000];

export default function SpCalci() {
  const [blankCost, setBlankCost] = useState("");
  const [quantity, setQuantity] = useState("");
  const [locations, setLocations] = useState(1);
  const [colorsPerLocation, setColorsPerLocation] = useState(["1"]);
  const [profitMargin, setProfitMargin] = useState(0.25);
  const [customMargin, setCustomMargin] = useState("");
  const [totalPrice, setTotalPrice] = useState(null);
  const [perItemCost, setPerItemCost] = useState(null);

  const getPricePerShirt = (qty, colors) => {
    let rangeIndex = 0;
    for (let i = 0; i < quantityRanges.length; i++) {
      if (qty >= quantityRanges[i]) {
        rangeIndex = i;
      }
    }
    const key = `${colors}C`;
    return priceTable[key]?.[rangeIndex] || 0;
  };

  const handleLocationColorChange = (index, value) => {
    const updated = [...colorsPerLocation];
    updated[index] = value;
    setColorsPerLocation(updated);
  };

  const handleCalculate = () => {
    const qty = parseFloat(quantity) || 0;
    const blank = parseFloat(blankCost) || 0;
    const actualMargin = customMargin !== "" ? parseFloat(customMargin) / 100 : profitMargin;

    const screenCharge = colorsPerLocation.reduce((sum, c) => sum + (parseFloat(c) || 0) * 25, 0);
    const printingCost = colorsPerLocation.reduce((sum, c) => sum + getPricePerShirt(qty, parseFloat(c) || 0), 0);
    const basePrice = (blank + printingCost) * qty + screenCharge;
    const finalPrice = basePrice * (1 + actualMargin);
    setTotalPrice(finalPrice.toFixed(2));

    if (qty > 0) {
      setPerItemCost((finalPrice / qty).toFixed(2));
    } else {
      setPerItemCost(null);
    }
  };

  const handleLocationsChange = (value) => {
    const num = parseInt(value) || 1;
    setLocations(num);
    setColorsPerLocation(Array(num).fill("1"));
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text("Screen Printing Price Quote", 10, 10);
    doc.text(`Blank Cost: $${blankCost}`, 10, 20);
    doc.text(`Quantity: ${quantity}`, 10, 30);
    colorsPerLocation.forEach((color, idx) => {
      doc.text(`Location ${idx + 1} Colors: ${color}`, 10, 40 + idx * 10);
    });
    doc.text(`Profit Margin: ${profitMargin === -1 ? customMargin + "%" : profitMargin * 100 + "%"}`, 10, 50 + colorsPerLocation.length * 10);
    if (totalPrice) {
      doc.text(`Total Price: $${totalPrice}`, 10, 60 + colorsPerLocation.length * 10);
      doc.text(`Per Item Cost: $${perItemCost}`, 10, 70 + colorsPerLocation.length * 10);
    }
    doc.save("screen_printing_quote.pdf");
  };

  return (
    <div className="container" style={{
      maxWidth: 600,
      margin: "0 auto",
      paddingTop: 30,
      paddingBottom: 50,
      backgroundColor: "#f9f9f9",
      borderRadius: 8,
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
    }}>
      <h1 className="text-3xl font-bold mb-4 text-center">Screen Printing Calculator</h1>

      <div className="form-group mb-3">
        <label>Blank Cost ($):</label>
        <input type="number" step="0.01" className="form-control" value={blankCost} onChange={(e) => setBlankCost(e.target.value)} />
      </div>

      <div className="form-group mb-3">
        <label>Quantity:</label>
        <input type="number" step="0.01" className="form-control" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </div>

      <div className="form-group mb-3">
        <label>Number of Locations:</label>
        <input type="number" min="1" className="form-control" value={locations} onChange={(e) => handleLocationsChange(e.target.value)} />
      </div>

      {colorsPerLocation.map((color, idx) => (
        <div className="form-group mb-3" key={idx}>
          <label>Colors at Location {idx + 1}:</label>
          <input type="number" step="0.01" className="form-control" value={color} onChange={(e) => handleLocationColorChange(idx, e.target.value)} />
        </div>
      ))}

      <div className="form-group mb-3">
        <label>Profit Margin:</label>
        <select className="form-control" value={profitMargin} onChange={(e) => setProfitMargin(parseFloat(e.target.value))}>
          <option value={0.25}>25%</option>
          <option value={0.35}>35%</option>
          <option value={0.45}>45%</option>
          <option value={0.5}>50%</option>
          <option value={-1}>Custom</option>
        </select>
      </div>

      {profitMargin === -1 && (
        <div className="form-group mb-3">
          <label>Custom Margin (%):</label>
          <input type="number" step="0.01" className="form-control" value={customMargin} onChange={(e) => setCustomMargin(e.target.value)} />
        </div>
      )}

      <div className="text-center mb-4">
        <button className="btn btn-primary btn-lg" onClick={handleCalculate}>
          Calculate Price
        </button>
      </div>

      {totalPrice && (
        <div className="alert alert-success text-center">
          <h4>Total Price: ${totalPrice}</h4>
          <h5>Per Item Cost: ${perItemCost}</h5>
        </div>
      )}

      {totalPrice && (
        <div className="text-center">
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            Download PDF Quote
          </button>
        </div>
      )}
    </div>
  );
}
